import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { Location } from '@angular/common';
import { NavigationStart, Router, ActivatedRoute } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError  } from 'rxjs/operators';
import { AuthService } from 'src/app/services/auth.service';
import { SettingsService } from 'src/app/services/settings.service';
import { SsoService } from 'src/app/services/sso.service';

import { UserToken } from 'src/app/shared/models/users/user-token';
import { UserTokenResult } from 'src/app/shared/models/users/user-token-result';

@Component({
  selector: 'app-navigation',
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.scss']
})
export class NavigationComponent implements OnInit {
  
  @ViewChild('sidenav', {static: false}) sidenav: ElementRef;
  @ViewChild('minimiseMenuBtn', {static: false}) minimiseMenuBtn: ElementRef;
  @ViewChild('logoLarge', {static: false}) logoLarge: ElementRef;
  @ViewChild('logoSmall', {static: false}) logoSmall: ElementRef;

  userToken: UserToken = {
      userId: 0,
      otp: null,
      redirectUrl: null,
      token: null,
      tokenExpiry: null
  };

  showTextInHeader: boolean = true;
  headerText = "Trading Portal";  
  showMenus: boolean = true;   

  constructor(
    private router: Router,
    public settingsService: SettingsService,
    private authService: AuthService,
    private ssoService: SsoService,
    private location: Location
  ) {
    
  }

  ngOnInit(): void {        
    const params = new URLSearchParams(window.location.search);
    const returnUrl = params.get('returnUrl'); 
    const initialPath = this.location.path(false).split('?')[0]; // no query
    const isLogoutNow = initialPath === '/logout' || initialPath.startsWith('/logout/');

    if (isLogoutNow) {
      this.showMenus = false;
      return;
    }    
    
    this.checkHasValidToken(returnUrl).subscribe({
        next: (tokenIsValid: boolean) => {
          if (tokenIsValid) {
            this.showMenus = true;
            if (returnUrl) {
              this.doReturnUrlPath(returnUrl);
              return;
            }

            this.settingsService.ValidateGlobalVars();
          } else {
            this.Logout();
          }
        },
        error: (err) => {
          console.error('An unexpected error occurred:', err);
          this.Logout();
        }
      });         
  }

  checkHasValidToken(returnUrl: string | null): Observable<boolean> {
    this.showMenus = false;

    if (!this.authService.hasValidTokenStored()) {
      return of(false);
    }

    // Normal login: auth guard already validated local token — no SSO round-trip.
    if (!returnUrl) {
      return of(true);
    }

    const localToken = localStorage.getItem('token');
    const localTokenExpires = localStorage.getItem('tokenExpires');
    const localUserId = localStorage.getItem('userId');

    if (!localToken || !localTokenExpires || !localUserId) {
      return of(false);
    }

    return this.ssoService.IsValidAuthToken(
      localToken,
      localTokenExpires,
      parseInt(localUserId, 10)
    ).pipe(
      catchError((err) => {
        console.log(`AuthService IsValidToken Error: ${err}`);
        return of(false);
      })
    );
  }

  doReturnUrlPath(returnUrl : string){ 
    this.showMenus = false;   
    this.userToken.userId = Number(localStorage.getItem('userId') ?? 0);
    this.userToken.redirectUrl = returnUrl;

    this.ssoService.RecheckUser(this.userToken).subscribe({
      next: (result: UserTokenResult) => {
        if (result.ssoRedirectUrl) {
          const url = new URL(result.ssoRedirectUrl);
          url.searchParams.set('authToken', result.ssoAuthToken);
          window.location.href = url.toString();
        } else {
          console.log('No valid redirect url found, logging out');
          this.Logout();
        }
      },
      error: () => {
        console.log('Recheck token failed');
        this.Logout();
      }
    });
  }

  ngDoCheck(){
    this.router.events.subscribe((e) => {
      if (e instanceof NavigationStart) {
        if (e.url === "/settings" || e.url === "/broker-account") {          
          this.headerText = e.url === "/settings"? "Settings" : "Manage Broker Users";
        }else{
          this.headerText = "Trading Portal";
        }
      }
    })
  }

  menuClicked() {
    if(window.innerWidth<=1024 && this.settingsService.sidenavExpanded){
      this.toggleSideNav();
    }
  }
  
  toggleSideNav(){
    if (!this.sidenav || !this.sidenav.nativeElement) {
      return;
    }

    if(this.sidenav.nativeElement.className === "side-nav fixed wide"){
      this.settingsService.sidenavExpanded = false;
      this.sidenav.nativeElement.className = "side-nav fixed wide slim";
      this.minimiseMenuBtn.nativeElement.className = "sv-slim-icon fas fa-angle-double-right";
      this.logoLarge.nativeElement.className = "logo-wrapper waves-effect p-1 d-none";
      this.logoSmall.nativeElement.className = "waves-effect p-1";
    }
    else{
      this.settingsService.sidenavExpanded = true;
      this.sidenav.nativeElement.className = "side-nav fixed wide";
      this.minimiseMenuBtn.nativeElement.className = "sv-slim-icon fas fa-angle-double-left";
      this.logoLarge.nativeElement.className = "logo-wrapper waves-effect p-1";
      this.logoSmall.nativeElement.className = "waves-effect p-1 d-none";
    }
  }

  Logout() {
    this.authService.beginSignOut();
    this.router.navigate(['/logout']);
  }  
}
